// The companion header's back link, when a hosted page has a level of its
// own. By default a hosted page's header goes back to More (or, on a deep
// path, to the page's list); a page that opens something in place (Projects
// opens a project with ?project=) claims the link while that is open, so
// the phone shows ONE back control ("‹ Projects") instead of the shell's
// "‹ More" above a second arrow of the page's own.
//
// Inside the iPhone app there is no header: the app draws the back button,
// so a claim is posted to it ({type: "back-claim", label}, label null when
// released) and the app runs it through window.latticeNative.back()
// (runCompanionBack, exposed by main.tsx).
import { useEffect } from "react";
import { create } from "zustand";

import { isEmbedded, postToNative } from "@/native/embedded";

export interface CompanionBack {
  /** Where the link goes, in its words: "Projects". */
  label: string;
  onBack: () => void;
}

const useBackStore = create<{ back: CompanionBack | null }>(() => ({ back: null }));

// Posted at the end of the task, not on every change: a claim whose onBack
// changed is released and taken again in one commit, and the app needs
// only where it ended up.
let postedLabel: string | null = null;
let postQueued = false;
useBackStore.subscribe(() => {
  if (postQueued || !isEmbedded()) return;
  postQueued = true;
  queueMicrotask(() => {
    postQueued = false;
    const label = useBackStore.getState().back?.label ?? null;
    if (label === postedLabel) return;
    postedLabel = label;
    postToNative({ type: "back-claim", label });
  });
});

/**
 * Claim the companion header's back link while `back` is set; released on
 * unmount or when `back` goes null. Pass a stable `onBack` (useCallback).
 * Outside the companion nothing reads it, so desktop pages need not care.
 */
export function useCompanionBack(back: CompanionBack | null): void {
  const label = back?.label;
  const onBack = back?.onBack;
  useEffect(() => {
    if (!label || !onBack) return;
    const claim = { label, onBack };
    useBackStore.setState({ back: claim });
    return () => {
      // Only release our own claim: a page mounting as this one unmounts
      // may already have set its own.
      if (useBackStore.getState().back === claim) useBackStore.setState({ back: null });
    };
  }, [label, onBack]);
}

/** The shell's read side: the page's claim, or null for the default. */
export function useCompanionBackClaim(): CompanionBack | null {
  return useBackStore((s) => s.back);
}

/**
 * Run the page's claimed back action: true when a page had claimed it,
 * false when nothing is claimed (the caller then goes back itself).
 */
export function runCompanionBack(): boolean {
  const back = useBackStore.getState().back;
  if (!back) return false;
  back.onBack();
  return true;
}
